
export class Color {
  r: number;
  g: number;
  b: number;
  a: number;

  constructor(value: number[]) {
    this.r = value[0];
    this.g = value[1];
    this.b = value[2];
    this.a = value.length > 3 ? value[3] : 1;
  }

  adjustSaturation(adjustment: number): Color {
    [this.r, this.g, this.b] = Color.adjustSaturation([this.r, this.g, this.b], adjustment);
    return this;
  }

  adjustBrightness(adjustment: number): Color {
    [this.r, this.g, this.b] = Color.adjustBrightness([this.r, this.g, this.b], adjustment);
    return this;
  }

  toHex() {
    return Color.toHex([this.r, this.g, this.b]);
  }

  toHsv() {
    return Color.rgbToHsv([this.r, this.g, this.b]);
  }

  toHsl() {
    return Color.rgbToHsl([this.r, this.g, this.b]);
  }
}

export namespace Color {
  const ERROR_RESULT = {};

  let _cache = new Map<string, any>();

  export function parseCssValue(colorString: string, fallback?: number[]): number[] {
    try {
      let value = _cache.get(colorString);
      if (value === ERROR_RESULT)
        throw new Error(`Invalid color value '${colorString}'`);

      if (value === undefined) {
        value = parseUsingCanvas(colorString);
        _cache.set(colorString, value);
      }

      return value;
    }
    catch (err) {
      return fallback;
    }
  }
  
  export function adjustSaturation(color: number[], adjustment: number): number[] {
    let hsv = rgbToHsv(color[0], color[1], color[2]);
    hsv[1] = Math.min(Math.max(hsv[1] + adjustment, 0), 1);
    return <number[]> (color.length > 3 ? [...hsvToRgb(hsv), color[3]] : hsv);
  }

  export function adjustBrightness(color: number[], adjustment: number): number[] {
    let hsv = rgbToHsv(color[0], color[1], color[2]);
    hsv[2] = Math.min(Math.max(hsv[2] + adjustment, 0), 1);
    return <number[]> (color.length > 3 ? [...hsvToRgb(hsv), color[3]] : hsv);
  }

  export function toHex(color: number[]): string {
    let s = color.map(x => {
      let n = Math.ceil(x);
      let s = n.toString(16);
      return n < 10 ? '0' + s : s;
    });

    return `#${s.join('')}`;
  }

  let _canvas: HTMLCanvasElement;
  let _canvasContext: CanvasRenderingContext2D;

  function parseUsingCanvas(colorString: string) {
    if (!_canvas) {
      _canvas = document.createElement('canvas');
      _canvas.width = 1;
      _canvas.height = 1;
      _canvasContext = _canvas.getContext('2d');
    }

    _canvasContext.clearRect(0, 0, 1, 1);
    _canvasContext.fillStyle = '#000';
    _canvasContext.fillStyle = colorString;

    let parsedValue = _canvasContext.fillStyle;
    _canvasContext.fillStyle = '#fff';
    _canvasContext.fillStyle = colorString;

    if (parsedValue !== _canvasContext.fillStyle) {
      throw new Error(`Invalid color value '${colorString}'`);
    }

    _canvasContext.fillRect(0, 0, 1, 1);
    return [..._canvasContext.getImageData(0, 0, 1, 1).data];
  }


  // REF: https://gist.github.com/mjackson/5311256
  /**
   * Converts an RGB color value to HSL. Conversion formula
   * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
   * Assumes r, g, and b are contained in the set [0, 255] and
   * returns h, s, and l in the set [0, 1].
   *
   * @param   Number  r       The red color value
   * @param   Number  g       The green color value
   * @param   Number  b       The blue color value
   * @return  Array           The HSL representation
   */
  export function rgbToHsl(rgbb: number[]): [number, number, number];
  export function rgbToHsl(r: number, g: number, b: number): [number, number, number];
  export function rgbToHsl(arg1: number |  number[], arg2?: number, arg3?: number): [number, number, number] {
    let r, g, b;
    if (typeof arg1 === 'number') {
      r = arg1;
      g = arg2;
      b = arg3;
    }
    else {
      [r, g, b] = arg1;
    }

    r /= 255, g /= 255, b /= 255;

    let max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max == min) {
      h = s = 0; // achromatic
    } else {
      let d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }

      h /= 6;
    }

    return [h, s, l];
  }

  /**
   * Converts an HSL color value to RGB. Conversion formula
   * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
   * Assumes h, s, and l are contained in the set [0, 1] and
   * returns r, g, and b in the set [0, 255].
   *
   * @param   Number  h       The hue
   * @param   Number  s       The saturation
   * @param   Number  l       The lightness
   * @return  Array           The RGB representation
   */
  export function hslToRgb(rgbb: number[]): [number, number, number];
  export function hslToRgb(h: number, s: number, l: number): [number, number, number];
  export function hslToRgb(arg1: number |  number[], arg2?: number, arg3?: number): [number, number, number] {
    let h, s, l;
    if (typeof arg1 === 'number') {
      h = arg1;
      s = arg2;
      l = arg3;
    }
    else {
      [h, s, l] = arg1;
    }

    let r, g, b;

    if (s == 0) {
      r = g = b = l; // achromatic
    } else {
      function hue2rgb(p, q, t) {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      }

      let q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      let p = 2 * l - q;

      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }

    return [r * 255, g * 255, b * 255];
  }

  /**
   * Converts an RGB color value to HSV. Conversion formula
   * adapted from http://en.wikipedia.org/wiki/HSV_color_space.
   * Assumes r, g, and b are contained in the set [0, 255] and
   * returns h, s, and v in the set [0, 1].
   *
   * @param   Number  r       The red color value
   * @param   Number  g       The green color value
   * @param   Number  b       The blue color value
   * @return  Array           The HSV representation
   */
  export function rgbToHsv(rgbb: number[]): [number, number, number];
  export function rgbToHsv(r: number, g: number, b: number): [number, number, number];
  export function rgbToHsv(arg1: number |  number[], arg2?: number, arg3?: number): [number, number, number] {
    let r, g, b;
    if (typeof arg1 === 'number') {
      r = arg1;
      g = arg2;
      b = arg3;
    }
    else {
      [r, g, b] = arg1;
    }

    r /= 255, g /= 255, b /= 255;

    let max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, v = max;

    let d = max - min;
    s = max == 0 ? 0 : d / max;

    if (max == min) {
      h = 0; // achromatic
    } else {
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }

      h /= 6;
    }

    return [h, s, v];
  }

  /**
   * Converts an HSV color value to RGB. Conversion formula
   * adapted from http://en.wikipedia.org/wiki/HSV_color_space.
   * Assumes h, s, and v are contained in the set [0, 1] and
   * returns r, g, and b in the set [0, 255].
   *
   * @param   Number  h       The hue
   * @param   Number  s       The saturation
   * @param   Number  v       The value
   * @return  Array           The RGB representation
   */
  export function hsvToRgb(rgbb: number[]): [number, number, number];
  export function hsvToRgb(h: number, s: number, v: number): [number, number, number];
  export function hsvToRgb(arg1: number |  number[], arg2?: number, arg3?: number): [number, number, number] {
    let h, s, v;
    if (typeof arg1 === 'number') {
      h = arg1;
      s = arg2;
      v = arg3;
    }
    else {
      [h, s, v] = arg1;
    }

    let r, g, b;

    let i = Math.floor(h * 6);
    let f = h * 6 - i;
    let p = v * (1 - s);
    let q = v * (1 - f * s);
    let t = v * (1 - (1 - f) * s);

    switch (i % 6) {
      case 0: r = v, g = t, b = p; break;
      case 1: r = q, g = v, b = p; break;
      case 2: r = p, g = v, b = t; break;
      case 3: r = p, g = q, b = v; break;
      case 4: r = t, g = p, b = v; break;
      case 5: r = v, g = p, b = q; break;
    }

    return [r * 255, g * 255, b * 255];
  }

  export function luma(rgb: number[]): number {
    return (0.2126 * rgb[0]) + (0.7152 * rgb[1]) + (0.0722 * rgb[2]); // SMPTE C, Rec. 709 weightings
  }
}
